import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components, operations } from "@api/schema";

/** The terminal statuses the backend accepts for the history `status` filter. */
export type PimsHistoryStatus = NonNullable<
  NonNullable<
    operations["get_pims_history_pims_history_get"]["parameters"]["query"]
  >["status"]
>;

/**
 * Audit filters on the settled-history view (WEB-3250, backend BE-2053). All optional
 * and server-side; omitted filters widen the page. `writesOnly` maps to `kind=write`,
 * `status` is one terminal status, `since`/`before` bound a period (ISO; `before` is
 * exclusive, doubling as the range end), and `recordUuid` narrows to one patient/client's
 * immutable record snapshot — so a later rename or delete never drops a task from its
 * record's history.
 */
export interface PimsHistoryFilters {
  writesOnly?: boolean;
  status?: PimsHistoryStatus;
  since?: string;
  before?: string;
  recordUuid?: string;
}

/** Whether any history filter is set — drives clear-affordance + empty copy. */
export const hasHistoryFilters = (f: PimsHistoryFilters): boolean =>
  Boolean(f.writesOnly || f.status || f.since || f.before || f.recordUuid);

/**
 * The primary patient/client identity a task is filed under (PIMS session
 * replay POC) — an immutable render-time snapshot, never a live join, so a
 * later rename/delete never drops the task from its record's grouping.
 */
export interface PimsRecordRef {
  kind: "patient" | "client";
  uuid: string;
  name: string;
}

/** The grouping key for a record — `${kind}:${uuid}` — stable across a
 * rename since `uuid` is the immutable snapshot identity. */
export const recordKey = (record: PimsRecordRef): string =>
  `${record.kind}:${record.uuid}`;

/** The staff-facing label for a record — `"Bella (patient)"` — shared by the
 * history grouping headers, the record filter's dropdown options, and the
 * audit replay header, so the three never drift out of sync. */
export const recordLabel = (record: PimsRecordRef): string =>
  `${record.name || "Unnamed"} (${record.kind})`;

/** One piece of live PIMS work Brian is running or has staged (BE-1953). */
export interface PimsActivityItem {
  id: string;
  kind: string;
  goal: string;
  status: string;
  /** Who performs the op (backend BE-2377): `cua` — a connector (Hans) drives
   * the PIMS GUI; `human` — clinic staff do the work themselves and check it
   * off. Defaults to `cua` for responses predating the field. */
  executor: "cua" | "human";
  subjectIds: string[];
  createdAt: string;
  updatedAt: string;
  deadlineAt: string | null;
  /** The write mode this op was staged under (null for a read, or a task
   * predating write-mode tracking). */
  writeMode: string | null;
  /** The primary patient/client this task is about, or null for a clinic-wide
   * op / a read of neither (PIMS session replay POC). */
  record: PimsRecordRef | null;
  /** This row's place in a retry chain (WEB-3270), both directions and each with
   * the other attempt's status. All null outside a chain. Server-resolved: the
   * failure and its retry are usually on different pages. */
  retryOf: string | null;
  retryOfStatus: string | null;
  retriedBy: string | null;
  retriedByStatus: string | null;
  /** Attestation (backend BE-2377), settled human rows only: the staff member
   * who checked the write off, and when. Name resolved server-side; null on
   * CUA rows, on human rows still awaiting staff, and after the completer is
   * offboarded (the timestamp survives on its own). */
  completedByName: string | null;
  completedAt: string | null;
}

/**
 * The clinic's live PIMS activity from `GET /pims/activity`. `queued` is only
 * ever populated in `batch` mode, so its emptiness — not a separate write-mode
 * field — is what drives whether the queued/flush framing renders.
 */
export interface PimsActivity {
  /** When the queued batch drains next; only set in `batch` mode. */
  nextFlushAt: string | null;
  /** Running / about-to-run work, oldest-first. */
  inFlight: PimsActivityItem[];
  /** Batch writes staged for the next flush, in flush order. */
  queued: PimsActivityItem[];
  /** Open manual-mode writes awaiting a staff check-off (backend BE-2377) —
   * their own bucket, never mixed into `inFlight`. */
  staff: PimsActivityItem[];
}

/**
 * The few staff-legible states we group the backend's status taxonomy into
 * (BE-1953). Live work is running/queued/held/offline (`held` arrives with Hans
 * concurrency, BE-1971, and already lands in `in_flight`); settled history work
 * is done/failed/cancelled/needs_input. Grouping keeps the UI legible rather
 * than mirroring the full backend taxonomy.
 */
export type ActivityState =
  | "running"
  | "queued"
  | "held"
  | "offline"
  | "done"
  | "failed"
  | "cancelled"
  | "needs_input"
  | "awaiting_staff";

/** Collapse an in-flight item's raw status into a staff-legible state. */
export const inFlightState = (item: PimsActivityItem): ActivityState =>
  item.status === "connector_offline"
    ? "offline"
    : item.status === "held"
      ? "held"
      : "running";

/**
 * Whether staff can cancel this item from the activity view (WEB-3227).
 * Queued batch items are always cancellable; an in-flight item only while it's
 * still `pending` or `held` (not yet dispatched to the clinic machine). Settled
 * history work never qualifies. A lost race just returns 409 — the UI doesn't
 * guard.
 *
 * A staff-executed row (`executor === "human"`) never qualifies: its whole
 * lifecycle runs through the pims_write card, and the backend's cancel can't
 * reach `awaiting_staff` anyway.
 */
export const isCancellable = (
  item: PimsActivityItem,
  state: ActivityState,
): boolean =>
  item.executor !== "human" &&
  (state === "queued" || item.status === "pending" || item.status === "held");

/**
 * Whether staff can re-run this settled item (WEB-3270). Mirrors the backend's
 * `RETRYABLE_STATUSES` (brian `modules/agent/hans_task_service.py`), which the
 * OpenAPI schema doesn't express — so the two must change together.
 *
 * Staff-executed rows are excluded even in a terminal failure status: retry
 * clones via the backend's `create_pending`, whose executor defaults to
 * autonomous — a write a human declined must not silently become a machine
 * write. Their only path is the pims_write card.
 */
export const isRetryable = (item: PimsActivityItem): boolean =>
  item.executor !== "human" &&
  (item.status === "failed" || item.status === "timeout");

/**
 * Whether re-running this item could duplicate work that already landed in the
 * PIMS — the confirm gate.
 *
 * Keyed on `kind`, deliberately not on which terminal failure it was: a PIMS op
 * is a multi-step automation with no transaction around it, so a `failed` write
 * is no likelier to have written nothing than a timed-out one (backend BE-2148).
 */
export const isPartialWriteRisk = (item: PimsActivityItem): boolean =>
  item.kind === "write";

/**
 * Collapse any raw status into a staff-legible state, for labelling the other end
 * of a retry chain — where all we have is the status string, not a whole item.
 */
export const stateForStatus = (status: string): ActivityState => {
  switch (status) {
    case "failed":
    case "timeout":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "needs_clarification":
      return "needs_input";
    case "done":
      return "done";
    case "connector_offline":
      return "offline";
    case "awaiting_staff":
      return "awaiting_staff";
    case "held":
      // `held`, not `queued`, to agree with `inFlightState` — otherwise the two
      // ends of one chain could describe the same row differently.
      return "held";
    default:
      return "running";
  }
};

/** Collapse a settled item's raw status into a staff-legible state. */
export const historyState = (item: PimsActivityItem): ActivityState => {
  switch (item.status) {
    case "failed":
    case "timeout":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "needs_clarification":
      return "needs_input";
    default:
      return "done";
  }
};

const toItem = (
  i: components["schemas"]["HansActivityOut"],
): PimsActivityItem => ({
  id: i.uuid,
  kind: i.kind,
  goal: i.goal,
  status: i.status,
  executor: i.executor === "human" ? "human" : "cua",
  subjectIds: i.subject_uuids,
  createdAt: i.created_at,
  updatedAt: i.updated_at,
  deadlineAt: i.deadline_ts,
  writeMode: i.write_mode ?? null,
  record: i.record
    ? { kind: i.record.kind, uuid: i.record.uuid, name: i.record.name ?? "" }
    : null,
  retryOf: i.retry_of ?? null,
  retryOfStatus: i.retry_of_status ?? null,
  retriedBy: i.retried_by ?? null,
  retriedByStatus: i.retried_by_status ?? null,
  completedByName: i.completed_by_name ?? null,
  completedAt: i.completed_at ?? null,
});

const toPimsActivity = (
  a: components["schemas"]["PimsActivityOut"],
): PimsActivity => ({
  nextFlushAt: a.next_flush_at,
  inFlight: a.in_flight.map(toItem),
  queued: a.queued.map(toItem),
  staff: (a.staff ?? []).map(toItem),
});

export const pimsActivityClient = {
  get: (): Promise<PimsActivity> =>
    request(() => apiClient.GET("/pims/activity", {})).then(toPimsActivity),

  // The most recent page of settled work (newest-first), narrowed by the audit
  // filters (WEB-3250). The cursor (`next_before`) is unused — one page is
  // enough for the activity tab.
  history: (filters: PimsHistoryFilters = {}): Promise<PimsActivityItem[]> =>
    request(() =>
      apiClient.GET("/pims/history", {
        params: {
          query: {
            kind: filters.writesOnly ? "write" : undefined,
            status: filters.status,
            since: filters.since,
            before: filters.before,
            record_uuid: filters.recordUuid,
          },
        },
      }),
    ).then((r) => r.items.map(toItem)),

  cancel: (id: string): Promise<PimsActivityItem> =>
    request(() =>
      apiClient.POST("/pims/activity/{task_uuid}/cancel", {
        params: { path: { task_uuid: id } },
      }),
    ).then(toItem),

  // Resolves to the FRESH `pending` task the backend staged, not the failed one
  // — the failure stays settled in history (backend BE-2178).
  retry: (id: string): Promise<PimsActivityItem> =>
    request(() =>
      apiClient.POST("/pims/activity/{task_uuid}/retry", {
        params: { path: { task_uuid: id } },
      }),
    ).then(toItem),
};
